import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { Patterns } from 'src/app/helpers/patterns';
import Swal from 'sweetalert2';
import { CheckOutDetails, CheckOutModel, CheckOutType, CheckOutResult, SplitByPrice, PaymentSourceTypeEnum } from './CheckOut.models';
import { CheckOutService } from './CheckOut.service';
import { CheckOutActions } from './CheckOutActions';
import { PaymentConfirmationComponent } from './payment-confirmation/payment-confirmation.component';
import { ActivatedRoute, Router } from '@angular/router';
import { Lookup } from 'src/app/models/Lookup';
import { Subscription } from 'rxjs';
import { SignalRService } from 'src/app/services/signalR/SignalR.service';
import { SignalEventType } from 'src/app/services/signalR/SignalModel';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-check-out',
  templateUrl: './check-out.component.html'
})
export class CheckOutComponent extends CheckOutActions implements OnInit, OnDestroy {
  dataLoading = true;
  @Input() orderId!: number;
  @Input() canSplit = true;
  @Output() close = new EventEmitter<boolean>();

  details!: CheckOutDetails;
  splitedItemsDetails!: CheckOutDetails;
  splitedPricesDetails!: CheckOutDetails;
  cashOptions: any;
  manualCachToPay!: number;

  isSplit = false;
  splitType = 'price'; // price - item
  floatPattern = Patterns.float2decimal;
  maxSplitPrice = 1;
  minSplitedPrice = 1;
  private unsubscribe$: Subject<any> = new Subject<any>();

  manualCash = false;
  CheckOutModel = new CheckOutModel();
  CheckOutResult!: CheckOutResult;
  checkoutId!: string;
  referencePaymentUrl!: string;
  paymentIframeLoader = true;

  otherPaymnetFormGroup!: FormGroup;

  hardReload = true;
  subscriptions: Subscription[] = [];

  isIOSDevice = this.iOS();

  getDetailsSubscriber!: Subscription;

  constructor(
    public dialog: MatDialog,
    checkoutService: CheckOutService,
    translate: TranslateService,
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private signal: SignalRService
  ) {
    super(checkoutService, translate);
    this.subscriptions.push(
      this.signal.getDataStream<string>(SignalEventType.updatePaymentSettings)
        .subscribe(message => { this.signalAction(message.data); })
    )
    this.subscriptions.push(
      this.signal.getDataStream<string>(SignalEventType.editOrder)
        .subscribe(message => { this.editOrderSignalAction(message.data); })
    )
    this.subscriptions.push(
      this.signal.getDataStream<string>(SignalEventType.OrderCheckOut)
        .subscribe(message => { this.CheckoutSignalAction(message.data); })
    )
    this.subscriptions.push(
      this.signal.getDataStream<string>(SignalEventType.QrCodeHyperPay)
        .subscribe(message => { this.signalPayAction(message.data); })
    )
    this.subscriptions.push(
      this.signal.getDataStream<string>(SignalEventType.NearPayNFC)
        .subscribe(message => { this.signalNFCPayAction(message.data); })
    )
  }

  signalAction(data: any) {
    const restId = Number(localStorage.getItem('restaurantId'))
    if (restId && data.RestaurantId == restId) {
      this.hardReload = false;
      this.signal.notify('Payment settings has been updated');
      this.getCheckoutDetails();
    }
  }
  signalPayAction(data: any) {
    const restId = Number(localStorage.getItem('restaurantId'))
    if (restId
      && data.RestaurantId == restId
      && (
        data.CheckoutId == this.checkoutId
        || data.OrderId == this.orderId
      )) {
      this.getCheckoutDetails();
    }
  }
  signalNFCPayAction(data: any) {
    const restId = Number(localStorage.getItem('restaurantId'));
    if (
      restId
      && data.RestaurantId == restId
      && data.OrderId == this.orderId
    ) {
      this.CheckOutResult = new CheckOutResult();
      this.CheckOutResult.canPrint = (data.canPrint == 'True');
      this.CheckOutResult.orderId = data.OrderId;
      this.CheckOutResult.isTotalyPaid = true;
      this.checkOutDone();
    }
  }
  editOrderSignalAction(data: any) {
    const restId = Number(localStorage.getItem('restaurantId'))
    if (data.RestaurantId == restId && data.OrderId == this.orderId) {
      this.hardReload = false;
      this.getCheckoutDetails();
    }
  }
  CheckoutSignalAction(data: any) {
    if (data.OrderId == this.orderId) {
      this.getCheckoutDetails();
    }
  }

  ngOnInit(): void {
    this.CheckOutModel.checkOutType = CheckOutType.Total;
    this.currentOrderId = this.orderId;
    // this.checkoutService.refreshView.next(true);
    this.activatedRoute.queryParamMap.subscribe(params => {
      if (params.get('id')) {
        this.checkoutId = params.get('id') || '';
        this.checkoutService.refreshView.next(false)
        //   this.checkPaymentStatues();
        // } else {
        //   // get details in normal mode
        //   this.getCheckoutDetails();
      }
    });
    this.getCheckoutDetails();

    this.checkoutService.refreshView.pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        if (data && !this.CheckOutResult?.isTotalyPaid) {
          this.getCheckoutDetails();
        }
      });
    this.checkoutService.checkOutDone.pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        if (data) {
          this.checkOutDone();
        }
      });
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
    this.checkoutService.refreshView.next(false);
  }
  getCheckoutDetails() {
    // this.getDetailsSubscriber?.unsubscribe();
    this.dataLoading = true;
    this.getDetailsSubscriber = this.checkoutService.ResGetOrderPaymentById(this.orderId)
      .subscribe(data => {
        this.dataLoading = false;
        this.details = data as CheckOutDetails;
        this.cashOptions = this.details.cashOptions;
        this.manualCachToPay = this.details.toBePaid;
        this.currency = this.details.currency;
        this.maxSplitPrice = this.details.partySize + 10;

        // reseting view
        if (!this.details.isTotalyPaid) {
          let checkOutType = CheckOutType.Total;
          if (this.details.splitPriceList.length) { // splited prices
            checkOutType = CheckOutType.SplitPrice;
          } else { // splited items
            let paidItems = this.details.splitItemList.filter((item: any) => item.splitedQuantity);
            if (paidItems.length) {
              checkOutType = CheckOutType.SplitItem;
            }
          }
          if (checkOutType != CheckOutType.Total) {
            this.isSplit = true;
            this.splitType = (checkOutType == CheckOutType.SplitPrice ? 'price' : 'item');
          }
        }
        this.CheckOutModel = new CheckOutModel();
        this.CheckOutModel.orderId = this.orderId;
        this.CheckOutModel.checkOutType = CheckOutType.Total;
        ////////////////////////////////////

        this.details.splitItemList.map((item: any) => {
          item.active = false;
          item.splitQuantity = 0;
        });
        if (!this.details.splitPriceList?.length) {
          for (let index = 0; index < this.details.partySize; index++) {
            this.plusSplitPriceList()
          }
        } else {
          this.minSplitedPrice = this.details.splitPriceList.length;
        }
        this.details.splitPriceList.map((item: any) => {
          item.isValid = true;
        });
        this.checkoutId = ''; // reset to hide payment widget

        if (this.details.isTotalyPaid) {
          this.closeCheckout();
        }

        // check if there is a online payment process
        this.CheckOutResult = this.details.onlinePaymentPaid;
        if (this.details.onlinePaymentPaid?.result) {
          this.checkPaymentStatues();
        }

        this.selectedPaymentOpt = null;
        this.hardReload = true;
      }, err => {
        console.error(err);
      });
  }
  backToSplitView() {
    // this.getCheckoutDetails();
    this.cashOptions = this.details.cashOptions;
    this.manualCachToPay = this.details.toBePaid;
    this.CheckOutModel = new CheckOutModel();
    this.CheckOutModel.orderId = this.orderId;
    this.CheckOutModel.checkOutType = CheckOutType.Total;

    this.isSplit = true;
    this.checkoutId = ''; // reset to hide payment widget
  }
  closeSplit() {
    this.isSplit = false;
    this.CheckOutModel.checkOutType = CheckOutType.Total;
    this.checkoutId = ''; // reset to hide payment widget
  }

  get activeSplitedPrices() {
    return this.details.splitPriceList.filter(item => !item.isPaid)
  }
  minusSplitPriceList() {
    if (this.minSplitedPrice >= this.details.splitPriceList.length) return
    this.details.splitPriceList.splice(this.details.splitPriceList.length - 1, 1)
    this.handleSplitPriceList()
  }
  plusSplitPriceList() {
    this.details.splitPriceList.push(new SplitByPrice())
    this.handleSplitPriceList()
  }
  handleSplitPriceList() {
    this.activeSplitedPrices.forEach(item => {
      item.isValid = true;
      item.priceAmount = Math.floor((this.details.toBePaid / this.activeSplitedPrices.length) * 100) / 100
    })
    this.calcChange();
  }
  handleSplitAmountInput(item: SplitByPrice, ctr: any) {
    item.isValid = (
      !this.checkValid(item.priceAmount)
      || ctr.errors?.['pattern']
      || ctr.errors?.['maxlength']
    ) ? false : true
    // if (!item.isValid) return;

    let totalAmount = Number(this.details.toBePaid) - Number(item.priceAmount);
    this.activeSplitedPrices.forEach((elem, i) => {
      if (elem !== item) {
        elem.priceAmount = Math.floor((totalAmount / (this.activeSplitedPrices.length - 1)) * 100) / 100;
        elem.isValid = this.checkValid(elem.priceAmount)
      }
    })
    this.calcChange();
  }
  checkValid(priceAmount: number) {
    return (
      !Number(priceAmount)
      || (Number(priceAmount) >= this.details.toBePaid)
      || (Number(priceAmount) <= 0)
    ) ? false : true
  }
  calcChange() {
    setTimeout(() => {
      let totalAmount = this.activeSplitedPrices.map(item => Number(String(Number(item.priceAmount).toFixed(2)).replace('.', ''))).reduce((acc, cur) => acc + cur, 0) / 100;
      if (totalAmount < this.details.toBePaid) {
        let lastSplit = this.activeSplitedPrices[this.activeSplitedPrices.length - 1];
        if (lastSplit) {
          let diff = this.calcDecimals(this.details.toBePaid, totalAmount, '-');
          lastSplit.priceAmount = this.calcDecimals(lastSplit.priceAmount, diff, '+');
        }
      }
    }, 100);
  }
  calcDecimals(b: number, c: number, operation: string) {
    let b1 = b.toString().split(".")
    let b1_max = 0
    if (b1.length == 2) {
      b1_max = b1[1].length
    }
    let c1 = c.toString().split(".")
    let c1_max = 0
    if (c1.length == 2) {
      c1_max = c1[1].length
    }
    let max_len = b1_max > c1_max ? b1_max : c1_max
    let result = 0;
    if (operation == '-') { result = Number((b - c).toFixed(max_len)) }
    if (operation == '+') { result = Number((b + c).toFixed(max_len)) }
    if (operation == '*') { result = Number((b * c).toFixed(max_len)) }
    return result;
  }

  splitItemClicked(item: any) {
    if ((item.quantity == 1 || item.isWeight) && (item.quantity != item.splitedQuantity)) {
      item.active = !item.active
    }
  }

  paymentConfirmation(amount: number, type: string) {
    this.CheckOutModel.paidAmount = amount;

    if (type == 'cash') {
      this.selectedPaymentOpt = null;
      delete this.CheckOutModel.paymentType;
      delete this.CheckOutModel.paymentOptionsCode;
    }

    const dialogRef = this.dialog.open(PaymentConfirmationComponent, {
      width: '50rem',
      data: {
        currency: this.currency,
        amount, type,
        checkOutResult: this.CheckOutResult
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result?.accept) {
        this.manualCash = false;
        if (result?.accept == 'no-tip') {
          this.ResNoTip(result.checkOutResult.checkOutId);
        } else {
          if (type == 'cash' || type == 'houseAccount') {
            this.Checkout();
          }
          if (type == 'tip') {
            this.ResAddTip(result.checkOutResult.checkOutId, result.checkOutResult.tipValue);
          }
        }
      } else {
        if (type == 'tip') {
          this.checkOutDone();
        }
      }
    });
  }
  payCashManual(data: any) {
    if (data?.value) {
      this.paymentConfirmation(data.value, 'cash');
    } else {
      this.manualCash = false;
    }
    return;
    if (Number(data?.value) < Number(this.details.toBePaid)) {
      this.InSufficientValue();
    } else {
      if (data?.value) {
        this.paymentConfirmation(data.value, 'cash');
      } else {
        this.manualCash = false;
      }
    }
  }
  InSufficientValue(error = '') {
    if (!error) {
      error = this.translate.instant('checkout.The value entered is below the required amount to complete the payment transaction');
    }
    Swal.fire({
      title: this.translate.instant('swalMsg.InSufficientValue'),
      text: error,
      imageUrl: './assets/images/icons/orders/warning-ic.svg',
      imageWidth: 50,
      imageHeight: 50,
      confirmButtonText: this.translate.instant('swalMsg.I got it'),
      customClass: {
        actions: 'm-0 mt-5 justify-content-end',
      },
    });
  }
  Checkout() {
    this.dataLoading = true;
    if (localStorage.getItem('deviceId')) {
      this.CheckOutModel.deviceId = localStorage.getItem('deviceId');
      this.CheckOutModel.deviceType = localStorage.getItem('deviceType');
    }
    this.checkoutService.ResCheckOut(this.CheckOutModel)
      .subscribe(response => {
        this.dataLoading = false;
        if (response?.result?.success) {
          this.CheckOutResult = response;
          if (response.hasTips) {
            this.paymentConfirmation(response.tipValue, 'tip');
          } else {
            this.checkOutDone();
          }
        } else {
          Swal.fire({
            title: this.translate.instant('swalMsg.Failed'),
            icon: 'error',
            confirmButtonText: this.translate.instant('swalMsg.Ok'),
          });
        }
      }, error => {
        this.dataLoading = false;
        if (error.status == 400) {
          this.InSufficientValue(error.detail);
        } else {
          Swal.fire({
            title: this.translate.instant('swalMsg.Failed'),
            text: error.detail,
            icon: 'error',
            confirmButtonText: this.translate.instant('swalMsg.Ok'),
          });
        }
      });
  }
  checkOutDone() {
    this.manualCash = false;
    this.paymentConfirmation(0, 'done');
    if (this.CheckOutResult?.isTotalyPaid) {
      this.close.emit(true);
    }
    this.checkoutService.refreshView.next(true);
  }

  getSelectedItems() {
    return this.details.splitItemList.filter((item: any) => item.active);
  }
  getSplitedItemsDetails() {
    this.closeSplit();
    this.CheckOutModel.checkOutType = CheckOutType.SplitItem;

    this.dataLoading = true;
    let splitItemList: any = [];
    this.getSelectedItems().forEach((item: any) => {
      splitItemList.push({
        reservationMenuItemId: item.id,
        splitQuantity: item.isWeight ? Number(item.quantity) : Number(item.splitQuantity || 1)
      })
    })
    let data = {
      orderId: this.orderId,
      splitItemList
    }
    this.CheckOutModel.splitItemList = splitItemList;
    this.checkoutService.ResCheckOutSplitItemsDetails(data)
      .subscribe(response => {
        this.dataLoading = false;
        this.splitedItemsDetails = response as CheckOutDetails;
        this.cashOptions = this.splitedItemsDetails.cashOptions;
        this.manualCachToPay = this.splitedItemsDetails.total;
      }, error => {
        this.dataLoading = false;
        Swal.fire({
          title: this.translate.instant('swalMsg.Failed'),
          text: error.detail,
          icon: 'error',
          confirmButtonText: this.translate.instant('swalMsg.Ok'),
        });
      });
  }
  getSplitedPriceDetails(item: SplitByPrice) {
    this.closeSplit();
    this.details.splitPriceList.map(one => { one.payNow = false })
    item.payNow = true;

    this.CheckOutModel.checkOutType = CheckOutType.SplitPrice;
    this.CheckOutModel.splitPriceList = this.details.splitPriceList;

    this.dataLoading = true;
    let data = {
      orderId: this.orderId,
      splitAmount: item.priceAmount
    }
    this.checkoutService.ResCheckOutSplitPriceDetails(data)
      .subscribe(response => {
        this.dataLoading = false;
        this.splitedPricesDetails = response as CheckOutDetails;
        this.cashOptions = this.splitedPricesDetails.cashOptions;
        this.manualCachToPay = this.splitedPricesDetails.total;
      }, error => {
        this.dataLoading = false;
        Swal.fire({
          title: this.translate.instant('swalMsg.Failed'),
          text: error.detail,
          icon: 'error',
          confirmButtonText: this.translate.instant('swalMsg.Ok'),
        });
      });
  }

  //////////////////////////////////////////////////////////////////////////
  checkoutIdRes: any;
  paymentWay = 'qrCode'; // qrCode || system
  selectedPaymentOpt!: Lookup | null;

  prepareCheckOut() {
    this.checkoutId = '';
    this.otherPaymnetFormGroup?.reset();

    this.details.paymentOptions.map(item => item.isUsed = false);

    if (!this.selectedPaymentOpt) return;
    this.selectedPaymentOpt.isUsed = true;

    this.CheckOutModel.paymentType = this.selectedPaymentOpt.lookupCode;
    this.CheckOutModel.paymentOptionsCode = this.selectedPaymentOpt.lookupCode;


    if (this.CheckOutModel.checkOutType == CheckOutType.Total) {
      this.CheckOutModel.paidAmount = this.details.toBePaid;
    }
    if (this.CheckOutModel.checkOutType == CheckOutType.SplitPrice) {
      this.CheckOutModel.paidAmount = this.splitedPricesDetails.total;
    }
    if (this.CheckOutModel.checkOutType == CheckOutType.SplitItem) {
      this.CheckOutModel.paidAmount = this.splitedItemsDetails.total;
    }

    if (localStorage.getItem('deviceId')) {
      this.CheckOutModel.deviceId = localStorage.getItem('deviceId');
      this.CheckOutModel.deviceType = localStorage.getItem('deviceType');
    }

    if (this.selectedPaymentOpt.lookupCode == 'HouseAccount') {
      this.paymentConfirmation(this.CheckOutModel.paidAmount, 'houseAccount');
      return;
    }

    if (this.selectedPaymentOpt.lookupCode == 'Other') {
      this.buildOtherPaymentForm();
    } else if (this.selectedPaymentOpt.lookupCode == 'NearPayNFC') {
      this.ResGetNFCSDK();
    } else {
      this.ResGetCheckoutId();
    }
  }

  ResGetCheckoutId() {
    this.paymentIframeLoader = true;
    this.actionLoading = true;
    let redirectUrl = window.location.origin;
    if (this.paymentWay == 'qrCode') {
      redirectUrl += '/pay?Type=Reservation';
    }
    if (this.paymentWay == 'system') {
      redirectUrl = window.location.href;
      if (redirectUrl.includes('?')) {
        redirectUrl += '&';
      } else {
        redirectUrl += '?';
      }
      redirectUrl += 'orderId=' + this.orderId;
    }
    this.CheckOutModel.CheckOutRedirectUrl = redirectUrl;
    this.CheckOutModel.paymentSourceType = this.paymentWay == 'qrCode' ? PaymentSourceTypeEnum.ClientQrCode : PaymentSourceTypeEnum.System;

    this.checkoutService.ResGetCheckoutId(this.CheckOutModel).subscribe(resp => {
      this.actionLoading = false;
      this.checkoutIdRes = resp;
      if (resp && resp.isSuccess && this.selectedPaymentOpt) {
        this.checkoutId = resp?.checkoutId;
        this.referencePaymentUrl = `./assets/paymentWidget.html?checkoutId=${this.checkoutId}&brand=${this.selectedPaymentOpt.lookupCode}&scriptUrl=${resp.url}`;
      } else {
        Swal.fire({
          title: this.translate.instant('swalMsg.Failed'),
          text: resp.message,
          icon: 'error',
          confirmButtonText: this.translate.instant('swalMsg.Ok'),
        });
      }
      setTimeout(() => { document.querySelector(".pay-methods .active")?.scrollIntoView() }, 100);
    }, err => {
      Swal.fire({
        title: this.translate.instant('swalMsg.Failed'),
        // text: this.translate.instant('swalMsg.Transaction failed!'),
        icon: 'error',
        confirmButtonText: this.translate.instant('swalMsg.Ok'),
      });
    });
  }
  ResGetNFCSDK() {
    this.actionLoading = true;
    this.checkoutService.ResGetNFCSDK(this.CheckOutModel).subscribe(resp => {
      this.actionLoading = false;
      this.checkoutIdRes = resp;
      setTimeout(() => { document.querySelector(".pay-methods .active")?.scrollIntoView() }, 100);
    }, err => {
      Swal.fire({
        title: this.translate.instant('swalMsg.Failed'),
        text: err.message,
        icon: 'error',
        confirmButtonText: this.translate.instant('swalMsg.Ok'),
      });
    });
  }
  checkPaymentStatues() {
    if (this.CheckOutResult?.result.code == 1) {
      this.checkOutDone();
    } else {
      Swal.fire({
        title: this.translate.instant('swalMsg.Failed'),
        text: this.translate.instant('swalMsg.Transaction failed!'),
        icon: 'error',
        confirmButtonText: this.translate.instant('swalMsg.Ok'),
      });
    }
    this.setQueryParams(); // This will call get check out details after remove chackoutId param
  }
  checkPaymentStatues_() {
    this.actionLoading = true;
    this.checkoutService.ResGetCheckoutStatus(this.checkoutId).subscribe(resp => {
      this.actionLoading = false;
      this.CheckOutResult = resp;
      this.checkOutDone();
      this.setQueryParams(); // This will call get check out details after remove chackoutId param
    }, err => {
      this.actionLoading = false;
      this.setQueryParams();
      Swal.fire({
        title: this.translate.instant('swalMsg.Failed'),
        text: this.translate.instant('swalMsg.Transaction failed!'),
        icon: 'error',
        confirmButtonText: this.translate.instant('swalMsg.Ok'),
      });
    });
  }
  setQueryParams() {
    const rIndex = location.search.indexOf("&orderId") || location.search.indexOf("?orderId")
    const searchParams = location.search.substring(0, rIndex).replace('?', '');
    let queryParams = {};
    if (searchParams) {
      queryParams =
        JSON.parse(
          '{"' + searchParams.replace(/&/g, '","').replace(/=/g, '":"') + '"}',
          function (key, value) { return key === "" ? value : decodeURIComponent(value) }
        );
    }
    this.router.navigate(
      [location.pathname],
      {
        relativeTo: this.activatedRoute,
        queryParams: queryParams
      });
  }
  closeCheckout() {
    this.setQueryParams();
    this.close.emit();
  }

  /*****************
   * Other payment form
  */
  otherPaymentFormLoader = false;
  buildOtherPaymentForm() {
    this.otherPaymnetFormGroup = this.formBuilder.group({
      otherPaymentOptionsId: [null, Validators.required],
      otherPaymentOptionsComment: [null, [
        // CustomValidator.noWhiteSpace,
        Validators.maxLength(400)
      ]],
    });
  }
  PaymnetFormError(controlName: string, errorName: string) {
    return this.otherPaymnetFormGroup.controls[controlName].hasError(errorName);
  }
  otherPaymentCheckout() {
    if (this.otherPaymnetFormGroup.valid) {
      this.CheckOutModel.otherPaymentOptionsId = this.otherPaymnetFormGroup.controls['otherPaymentOptionsId'].value;
      this.CheckOutModel.otherPaymentOptionsComment = this.otherPaymnetFormGroup.controls['otherPaymentOptionsComment'].value;
      this.otherPaymentFormLoader = true;
      this.Checkout();
    }
  }

  iOS() {
    return [
      'iPad Simulator',
      'iPhone Simulator',
      'iPod Simulator',
      'iPad',
      'iPhone',
      'iPod'
    ].includes(navigator.platform)
      // iPad on iOS 13 detection
      || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  }
}
